import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  upsertFetcherInterface,
  upsertFetcherObjects,
} from "../../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { FetcherObject } from "../helpers/fetcherSettingsDummyData";
import { nextLocalId } from "../helpers/localIds";
import { nowStamp } from "../helpers/formatters";
import ObjectsPanel from "../ObjectsPanel";

export interface InterfaceDraftSeed {
  name: string;
  dvar: string;
  objects: { k: string; v: string }[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** pre-fills the form - used by Duplicate, which copies name + objects */
  initial?: InterfaceDraftSeed | null;
  /** hands the new interface's id back to whatever opened this dialog */
  onCreated?: (interfaceId: string) => void;
}

const STEPS = ["Identity", "Objects"];

const toDraftObjects = (rows: { k: string; v: string }[]): FetcherObject[] =>
  rows.map((row, index) => ({
    id: `draft-${index + 1}`,
    lvl: "interface_id",
    raw: "interface_id",
    mid: "",
    k: row.k,
    v: row.v,
    ufr: "0",
    mod: "",
  }));

const CreateInterfaceModal = ({
  open,
  onClose,
  initial,
  onCreated,
}: Props) => {
  const dispatch = useDispatch();
  const { interfaces, objects } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [dynamicVar, setDynamicVar] = useState("");
  const [draftObjects, setDraftObjects] = useState<FetcherObject[]>([]);

  // Duplicate hands over a seed; a plain Create opens empty
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setName(initial?.name ?? "");
    setDynamicVar(initial?.dvar ?? "");
    setDraftObjects(toDraftObjects(initial?.objects ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const duplicate = interfaces.find(
    (row) => !!name && row.name.toLowerCase() === name.trim().toLowerCase(),
  );
  const isIdentityValid = !!name.trim() && !duplicate;

  const reset = () => {
    setStep(0);
    setName("");
    setDynamicVar("");
    setDraftObjects([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleCreate = () => {
    // TODO: POST /aladdin_api/v1/data_collection/fetcher_interface/ - returns
    // id + shared_uid; the id is the FK the next step in the chain needs
    const id = nextLocalId(interfaces);
    dispatch(
      upsertFetcherInterface({
        id,
        name: name.trim(),
        dvar: dynamicVar.trim(),
        reports: 0,
        configs: 0,
        brands: 0,
        objects: draftObjects.length,
        mod: nowStamp(),
      }),
    );
    if (draftObjects.length) {
      let objectId = Number(nextLocalId(objects));
      dispatch(
        upsertFetcherObjects(
          draftObjects.map((row) => ({
            ...row,
            id: String(objectId++),
            lvl: "interface_id",
            raw: "interface_id",
            mid: id,
            mod: nowStamp(),
          })),
        ),
      );
    }
    dispatch(
      setDisplayErrorSnackBar({
        message: `${name.trim()} (${id}) created.`,
        type: "success",
      }),
    );
    reset();
    onCreated?.(id);
    onClose();
  };

  const addDraftObject = (fieldType: string, fieldValue: string) =>
    setDraftObjects((previous) => [
      ...previous,
      {
        id: `draft-${Date.now()}`,
        lvl: "interface_id",
        raw: "interface_id",
        mid: "",
        k: fieldType,
        v: fieldValue,
        ufr: "0",
        mod: "",
      },
    ]);

  return (
    <Dialog
      open={open}
      onClose={close}
      PaperProps={{ sx: { width: "760px", maxWidth: "94vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>New Interface</DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 ? (
          <Stack spacing={2.5}>
            <TextField
              required
              fullWidth
              size="small"
              label="Interface Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              color="secondary"
            />
            <TextField
              fullWidth
              size="small"
              label="Common Dynamic Var"
              placeholder="optional"
              value={dynamicVar}
              onChange={(event) => setDynamicVar(event.target.value)}
              color="secondary"
            />
            {duplicate && (
              <Alert severity="error">
                <b>Blocked.</b> An interface named {duplicate.name} (
                {duplicate.id}) already exists.
              </Alert>
            )}
          </Stack>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Objects on this interface. object_type and object_value are set
              automatically once it is created — only field_type and field_value
              are needed.
            </Alert>
            <ObjectsPanel
              ownLabel="This interface"
              ownObjects={draftObjects}
              markedForDelete={[]}
              onAdd={addDraftObject}
              onEdit={(id, fieldType, fieldValue) =>
                setDraftObjects((previous) =>
                  previous.map((row) =>
                    row.id === id ? { ...row, k: fieldType, v: fieldValue } : row,
                  ),
                )
              }
              onMarkDelete={(id) =>
                setDraftObjects((previous) =>
                  previous.filter((row) => row.id !== id),
                )
              }
              onUndoDelete={() => undefined}
            />
            {draftObjects.length === 0 && (
              <Typography sx={{ fontSize: "12px", color: "#00000099", mt: 1 }}>
                Without an endpoint or parameters the collector must build every
                call for this interface in code.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step > 0 && (
          <Button color="secondary" onClick={() => setStep(step - 1)}>
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button color="secondary" onClick={close}>
          Cancel
        </Button>
        {step === 0 ? (
          <Button
            variant="contained"
            color="secondary"
            disabled={!isIdentityValid}
            onClick={() => setStep(1)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            disabled={!isIdentityValid}
            onClick={handleCreate}
          >
            Create interface
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateInterfaceModal;
